// A sensor in the desktop room grid — the same two states the mobile card
// shows, in this shell's language.
//
// It replaces what GenericControlCard did for these devices, which was to fetch
// their outputs, find none, and print "No controllable outputs" over a grey
// icon. That is true and useless: the device has plenty to say, it just has
// nothing to switch. Here the state is the card.
//
// Nothing is fetched. A sensor has no outputs to name and no channels to read,
// so the row's own `Status` plus the live topic is the whole story — which also
// means this card costs zero requests where the generic one cost one per sensor.

import React from 'react';
import { Card, Box, Typography } from '@mui/material';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import { parseStatus } from '../../../core/mqtt/mqttClient';
import {
  sensorStateOf, SENSOR_ALERT, SENSOR_NORMAL,
} from '../../../core/devices/sensorState';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';

// Fixed, not themed: a red/green pair a customer's palette could wash out would
// defeat the point of the card.
const COLOURS = {
  [SENSOR_NORMAL]: '#2E7D32',
  [SENSOR_ALERT]: '#C62828',
};
const NEUTRAL = '#9E9E9E';

export default function SensorControlCard({ device, subtitle }) {
  const uno = device.UniqueNumber;
  const [status, , liveAt] = useDeviceStatus(
    uno,
    parseStatus(device.Status || device.DeviceStatus || ''),
  );
  const { state, label } = sensorStateOf(status, device.DeviceID);
  const colour = COLOURS[state] || NEUTRAL;

  return (
    <Card sx={{
      p: 1.25,
      borderRadius: 3,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'divider',
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap>
            {device.DeviceName || uno}
          </Typography>
        </Box>
        <DeviceStatusDot device={device} live={liveAt} />
        <DeviceCardMenu device={device} />
      </Box>

      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        py: 0.75,
      }}
      >
        <Box sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: colour,
          boxShadow: `0 0 0 6px ${colour}22`,
        }}
        />
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ color: colour, textAlign: 'center', textTransform: 'uppercase', lineHeight: 1.2 }}
        >
          {label}
        </Typography>
      </Box>
    </Card>
  );
}
